import { useEffect, useRef, useState } from "react";
import { useSettingsStore } from "../state/settingsStore";
import type { ChartHandle } from "./Chart";
import type { DrawingToolId, IndicatorId } from "./chartVocabulary";

/**
 * Wires the TA workbench for a view: a Chart handle ref, the indicator set from
 * settings, tool/clear/toggle callbacks, and auto-clearing of drawings when the
 * Round changes (drawings persist through the reveal, since the Round id is
 * stable across it — spec §8).
 */
export function useTAWorkbench(roundId: string | undefined) {
  const chartRef = useRef<ChartHandle>(null);
  const indicators = useSettingsStore((s) => s.indicators);
  const setIndicators = useSettingsStore((s) => s.setIndicators);

  // Which tool is armed, for the toolbar's pressed state. The Chart owns the
  // truth (only it sees the drawing finish or get cancelled) and reports it
  // back through `onDrawingChange`.
  const [activeTool, setActiveTool] = useState<DrawingToolId | null>(null);

  // Clear drawings on each new Round (not on reveal — same Round id).
  useEffect(() => {
    chartRef.current?.clearDrawings();
  }, [roundId]);

  return {
    chartRef,
    indicators,
    activeTool,
    onDrawingChange: setActiveTool,
    onTool: (toolId: DrawingToolId) => chartRef.current?.startDrawing(toolId),
    onClear: () => chartRef.current?.clearDrawings(),
    onToggleIndicator: (id: IndicatorId) =>
      setIndicators(
        indicators.includes(id)
          ? indicators.filter((i) => i !== id)
          : [...indicators, id],
      ),
  };
}
